import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MenuModule } from 'primeng/menu';
import { IDispatchCampaign } from '@common-interfaces/admin/crm/dispatch-campaign';
import { debugLogger } from '@common-helpers/debug-logger';
import { Api } from '../../../../services/api.service';
import { Utils } from '../../../../services/utils.service';
import { AuthService } from '../../../../services/auth.service';
import { ToastService } from '../../../../services/toast.service';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { EmptyStateComponent } from '../../../../components/shared/empty-state/empty-state.component';
export interface IDispatchCampaignWithResults extends IDispatchCampaign {
    results?: {
        email: {
            sent: number;
            delivered: number;
            opened: number;
            clicked: number;
            bounced: number;
        };
        sms: {
            sent: number;
            delivered: number;
            failed: number;
        };
    };
}

@Component({
    selector: 'dispatch-list',
    imports: [
        FormsModule,
        TableModule,
        SkeletonModule,
        TranslatePipe,
        ButtonModule,
        TagModule,
        TooltipModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        ToggleSwitchModule,
        MenuModule,
        DatePipe,
        PageHeaderComponent,
        EmptyStateComponent
    ],
    templateUrl: './dispatch-list.component.html'
})
export class DispatchListComponent implements OnInit {
    api = inject(Api);
    utility = inject(Utils);
    private router = inject(Router);
    private auth = inject(AuthService);
    private toast = inject(ToastService);
    private translate = inject(TranslateService);
    private confirm = inject(ConfirmationService);

    loading = signal<boolean>(true);
    creating = signal<boolean>(false);
    search = signal('');
    showRemoved = signal(false);
    dispatches = signal<IDispatchCampaignWithResults[]>([]);

    removedCount = computed(() => this.dispatches().filter((c) => c.disabled).length);
    visible = computed(() => {
        const term = this.search().trim().toLowerCase();
        return this.dispatches().filter(
            (c) => (this.showRemoved() || !c.disabled) && (!term || c.name?.toLowerCase().includes(term))
        );
    });

    async ngOnInit(): Promise<void> {
        await this.load();
    }

    private async load(): Promise<void> {
        try {
            const campaigns = await this.api.crm().get<IDispatchCampaignWithResults[]>('/dispatches');
            if (campaigns.success && campaigns.data) {
                this.dispatches.set(campaigns.data);
            }
        } catch (error) {
            debugLogger.error('Failed to load campaigns', error);
            this.toast.showError(this.translate.instant('ERR_GENERIC'));
        } finally {
            this.loading.set(false);
        }
    }

    openDispatch(id: string): void {
        this.router.navigate(['/organizations', this.auth.organizationId(), 'crm', 'campaigns', id]);
    }

    async newCampaign(): Promise<void> {
        this.creating.set(true);
        try {
            const res = await this.api.crm().post<IDispatchCampaign>('/dispatches/new', {});
            if (res.success && res.data?.id) {
                this.openDispatch(res.data.id);
            }
        } catch (error) {
            debugLogger.error('Failed to create campaign', error);
            this.toast.showError(this.translate.instant('ERR_GENERIC'));
        } finally {
            this.creating.set(false);
        }
    }

    canRemove(campaign: IDispatchCampaign): boolean {
        return campaign.status !== 'DISPATCHED' && campaign.status !== 'PROCESSING_DISPATCH';
    }

    rowMenu(campaign: IDispatchCampaignWithResults): MenuItem[] {
        const items: MenuItem[] = [
            { label: this.translate.instant('DUPLICATE'), icon: 'ph ph-copy', command: () => this.duplicate(campaign) }
        ];
        if (this.canRemove(campaign)) {
            items.push(
                campaign.disabled
                    ? { label: this.translate.instant('RESTORE'), icon: 'ph ph-arrow-counter-clockwise', command: () => this.setRemoved(campaign, false) }
                    : { label: this.translate.instant('REMOVE'), icon: 'ph ph-trash', styleClass: 'text-red-500', command: () => this.confirmRemove(campaign) }
            );
        }
        return items;
    }

    private async duplicate(campaign: IDispatchCampaign): Promise<void> {
        try {
            const res = await this.api.crm().post<IDispatchCampaign>(`/dispatches/${campaign.id}/clone`, {});
            if (res.success && res.data) {
                this.dispatches.update((list) => [res.data, ...list]);
                this.toast.showSuccess(this.translate.instant('CAMPAIGN_DUPLICATED'));
            }
        } catch (error) {
            debugLogger.error('Failed to duplicate campaign', error);
            this.toast.showError(this.translate.instant('ERR_GENERIC'));
        }
    }

    private confirmRemove(campaign: IDispatchCampaign): void {
        this.confirm.confirm({
            header: this.translate.instant('REMOVE_CAMPAIGN_TITLE'),
            message: this.translate.instant(campaign.isArmed ? 'REMOVE_CAMPAIGN_ARMED_TEXT' : 'REMOVE_CAMPAIGN_TEXT', { name: campaign.name }),
            icon: 'ph ph-trash',
            acceptLabel: this.translate.instant('REMOVE'),
            rejectLabel: this.translate.instant('CANCEL'),
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => this.setRemoved(campaign, true)
        });
    }

    private async setRemoved(campaign: IDispatchCampaign, disabled: boolean): Promise<void> {
        try {
            const res = await this.api.crm().post<IDispatchCampaign>(`/dispatches/${campaign.id}/disabled`, { disabled });
            if (res.success && res.data) {
                this.dispatches.update((list) => list.map((c) => (c.id === campaign.id ? { ...c, ...res.data } : c)));
                this.toast.showSuccess(this.translate.instant(disabled ? 'CAMPAIGN_REMOVED' : 'CAMPAIGN_RESTORED'));
            }
        } catch (error) {
            debugLogger.error('Failed to change campaign state', error);
            this.toast.showError(this.translate.instant('ERR_GENERIC'));
        }
    }
}
